"use client";

import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

export interface RevealProps {
  children: React.ReactNode;
  /** Stagger delay in milliseconds before the reveal transition begins. */
  delayMs?: number;
  /** Additional classes applied to the wrapper element. */
  className?: string;
}

/**
 * Reveal
 *
 * Wraps content in a scroll-triggered fade-and-rise. The children are fully
 * visible by default and motion only *enhances* them: the hidden state is armed
 * imperatively (via a `data-reveal` attribute) and only when JavaScript runs,
 * IntersectionObserver exists, and the user hasn't asked for reduced motion.
 * Without all three the attribute is never set, so the content renders at its
 * natural visible state and a section can never ship blank.
 *
 * Consolidates the ad-hoc IntersectionObserver reveals scattered across the
 * codebase (StatCard, TeamGrid) into one primitive.
 *
 * @atomic-tier Atom
 */
export function Reveal({ children, delayMs = 0, className }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (SSR fallback / unsupported): stay visible.
    if (typeof IntersectionObserver === "undefined") return;
    // Respect reduced-motion: never enter the hidden state.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    // Arm into the hidden state through the DOM — an effect's sanctioned job —
    // rather than React state, which would cascade a render on every mount.
    el.dataset.reveal = "hidden";
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          el.dataset.reveal = "shown";
          observer.disconnect();
        }
      },
      { rootMargin: "-40px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn("aq-reveal", className)}
      style={delayMs ? { transitionDelay: `${delayMs}ms` } : undefined}
    >
      {children}
    </div>
  );
}
